import { useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { createFilterOptions } from '@mui/material/Autocomplete';
import dayjs from 'dayjs';
import api from '../../../../utils/functions/api';
import { preprocessReceiptImage, preprocessOdometerImage } from '../../../../utils/imagePreprocess';
import * as actions from '../../../../redux/actions';
import { emptyFuelClaim } from '../constants';

const filterOptions = createFilterOptions();

// Local to this hook only (not a shared file) — converts a base64 data URL
// (already held in claimForm for preview/OCR) back into a File, resizing it
// first if oversized, so it can be uploaded via multipart instead of being
// bundled as base64 into the create-expense JSON body. Up to 7 photos
// (bill + odometer + 5 others) as base64 in one request easily blew past the
// backend's 15mb body limit → 413 Payload Too Large.
const MAX_IMAGE_DIMENSION = 2500;
const IMAGE_QUALITY = 0.9;

function dataUrlToFile(dataUrl, filename) {
  const [header, base64] = dataUrl.split(',');
  const mimeMatch = header.match(/data:([^;]+)/);
  const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

const resizeImageFileIfNeeded = (file) =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      const { width, height } = img;
      if (width <= MAX_IMAGE_DIMENSION && height <= MAX_IMAGE_DIMENSION) {
        URL.revokeObjectURL(objectUrl);
        resolve(file);
        return;
      }

      let targetWidth = width;
      let targetHeight = height;
      if (width >= height) {
        targetWidth = MAX_IMAGE_DIMENSION;
        targetHeight = Math.round((height * MAX_IMAGE_DIMENSION) / width);
      } else {
        targetHeight = MAX_IMAGE_DIMENSION;
        targetWidth = Math.round((width * MAX_IMAGE_DIMENSION) / height);
      }

      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      canvas.getContext('2d').drawImage(img, 0, 0, targetWidth, targetHeight);
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(objectUrl);
          resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file);
        },
        'image/jpeg',
        IMAGE_QUALITY,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file);
    };
    img.src = objectUrl;
  });

export function useFuelClaim({ allVehiclesForClaim, me, dispatch }) {
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimForm, setClaimForm] = useState(emptyFuelClaim);
  const [ocrScanning, setOcrScanning] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [ocrOdoScanning, setOcrOdoScanning] = useState(false);
  const [claimSubmitting, setClaimSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formErrors, setFormErrors] = useState({});
  const [plateNotFound, setPlateNotFound] = useState(null);
  const [claimSuccess, setClaimSuccess] = useState(false);
  const [openCustomerModal, setOpenCustomerModal] = useState(false);
  const [bank, setBank] = useState('');
  const [customerSubmitting, setCustomerSubmitting] = useState(false);

  const {
    control: customerControl,
    formState: { errors: customerErrors },
    handleSubmit: handleCustomerSubmit,
    reset: resetCustomer,
  } = useForm();

  const needsOdo = claimForm.vehicle?.owner_type === 'company';

  const openClaim = () => {
    setClaimForm({ ...emptyFuelClaim, vehicle: null });
    setOcrResult(null);
    setClaimOpen(true);
  };

  const handleScanBill = async () => {
    if (!claimForm.bill_image) return;
    setOcrScanning(true);
    setOcrResult(null);
    try {
      const base64 = await preprocessReceiptImage(claimForm.bill_image);
      const { data, status } = await api.post(
        `${process.env.REACT_APP_API_URL}/ai/analyze-fuel-receipt`,
        { image: base64, mimeType: 'image/jpeg' },
      );
      if (status !== 200) throw new Error('scan failed');
      const result = data;
      setOcrResult(result);
      const updates = {};
      if (result.date) updates.date = result.date;
      if (result.total_amount) updates.fuel_amount = String(result.total_amount);
      if (result.odometer) updates.odo_in = String(result.odometer);
      if (result.liters != null) updates.fuel_liters = String(result.liters);
      if (result.price_per_liter != null) updates.fuel_price_per_liter = String(result.price_per_liter);
      if (result.station_name) updates.fuel_station = result.station_name;
      if (result.vat_amount != null) updates.vat_amount = String(result.vat_amount);
      if (result.base_amount != null) updates.base_amount = String(result.base_amount);
      if (result.license_plate) updates.fuel_license_plate_in_bill = result.license_plate;
      if (result.fuel_type) updates.fuel_type_in_bill = result.fuel_type;
      if (result.bill_number) updates.invoice_number = result.bill_number;
      if (result.license_plate) {
        const normalizedPlate = result.license_plate.replace(/\s+/g, '').toLowerCase();
        const matched = allVehiclesForClaim.find(
          (v) => v.license_plate.replace(/\s+/g, '').toLowerCase() === normalizedPlate,
        );
        if (matched) {
          updates.vehicle = matched;
          setPlateNotFound(null);
        } else {
          setPlateNotFound({ plate: result.license_plate });
        }
      } else {
        setPlateNotFound({ plate: '' });
      }
      setClaimForm((prev) => ({ ...prev, ...updates }));
    } catch (e) {
      console.error('[FuelSetting] scan bill error:', e);
    } finally {
      setOcrScanning(false);
    }
  };

  const handleScanOdometer = async () => {
    if (!claimForm.odo_image) return;
    setOcrOdoScanning(true);
    try {
      const base64 = await preprocessOdometerImage(claimForm.odo_image);
      const { data, status } = await api.post(
        `${process.env.REACT_APP_API_URL}/ai/analyze-odometer`,
        { image: base64, mimeType: 'image/jpeg' },
      );
      if (status === 200 && data?.odometer) {
        setClaimForm((prev) => ({ ...prev, odo_in: String(data.odometer) }));
      }
    } catch (e) {
      console.error('[FuelSetting] scan odometer error:', e);
    } finally {
      setOcrOdoScanning(false);
    }
  };

  const handleProjectChange = useCallback((_, newVal) => {
    setClaimForm((prev) => ({ ...prev, project: newVal, budget: null }));
  }, []);

  const handleBudgetChange = useCallback((_, newVal) => {
    setClaimForm((prev) => ({ ...prev, budget: newVal }));
  }, []);

  const handleVehicleChange = useCallback((_, newVal) => {
    setClaimForm((prev) => ({ ...prev, vehicle: newVal }));
  }, []);

  const handleCustomerChange = useCallback((_, newVal) => {
    if (typeof newVal === 'string' || newVal?.isAddNew) {
      setOpenCustomerModal(true);
    } else {
      setClaimForm((prev) => ({
        ...prev,
        customer: newVal,
        payee: {
          name: newVal?.bank?.account_name || '',
          bank: newVal?.bank?.bank_name || '',
          account_number: newVal?.bank?.account_number || '',
        },
      }));
    }
  }, []);

  const handleExpensesTypeChange = useCallback((e) => {
    setClaimForm((prev) => ({ ...prev, expenses_type: e.target.value }));
  }, []);

  const handlePaymentDateChange = useCallback((e) => {
    setClaimForm((prev) => ({ ...prev, payment_date: e.target.value }));
  }, []);

  const handleOdoChange = useCallback((e) => {
    setClaimForm((prev) => ({ ...prev, odo_in: e.target.value.replace(/\D/g, '') }));
  }, []);

  const handleDateChange = useCallback((e) => {
    setClaimForm((prev) => ({ ...prev, date: e.target.value }));
  }, []);

  const handleAmountChange = useCallback((e) => {
    setClaimForm((prev) => ({ ...prev, fuel_amount: e.target.value }));
  }, []);

  const customerFilterOptions = useCallback((options, params) => {
    const filtered = filterOptions(options, params);
    const { inputValue } = params;
    const isExisting = options.some((o) => o.name?.toLowerCase() === inputValue.toLowerCase());
    if (inputValue !== '' && !isExisting) {
      filtered.push({ inputValue, name: inputValue, isAddNew: true });
    }
    return filtered;
  }, []);

  const handleOpenCustomerModal = () => setOpenCustomerModal(true);
  const handleCloseCustomerModal = () => {
    setOpenCustomerModal(false);
    resetCustomer();
    setBank('');
  };

  const onSubmitCustomer = async (data) => {
    if (customerSubmitting) return;
    setCustomerSubmitting(true);
    try {
      data.bank = {
        bank_name: bank,
        account_name: data?.bank?.account_name,
        account_number: data?.bank?.account_number,
      };
      const created = await dispatch(actions.customerCreate(data));
      await dispatch(actions.customerAll({}));
      handleCloseCustomerModal();
      if (created?.payload) {
        setClaimForm((prev) => ({
          ...prev,
          customer: created.payload,
          payee: {
            name: created.payload?.bank?.account_name || data?.bank?.account_name || '',
            bank: created.payload?.bank?.bank_name || bank || '',
            account_number: created.payload?.bank?.account_number || data?.bank?.account_number || '',
          },
        }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setCustomerSubmitting(false);
    }
  };

  const validateClaimForm = () => {
    const e = {};
    if (!claimForm.vehicle) e.vehicle = 'กรุณาเลือกทะเบียนรถ';
    if (!claimForm.date) e.date = 'กรุณาระบุวันที่เติมน้ำมัน';
    if (!claimForm.fuel_amount || Number(claimForm.fuel_amount) <= 0) e.fuel_amount = 'กรุณาระบุยอดเงินในบิล';
    if (!claimForm.bill_image) e.bill_image = 'กรุณาแนบรูปใบเสร็จน้ำมัน';
    if (!claimForm.fuel_liters || Number(claimForm.fuel_liters) <= 0) e.fuel_liters = 'กรุณาระบุจำนวนลิตร';
    if (!claimForm.fuel_price_per_liter || Number(claimForm.fuel_price_per_liter) <= 0) e.fuel_price_per_liter = 'กรุณาระบุราคาต่อลิตร';
    if (!claimForm.fuel_station?.trim()) e.fuel_station = 'กรุณาระบุชื่อสถานีบริการ';
    if (needsOdo) {
      if (!claimForm.odo_image) e.odo_image = 'กรุณาแนบรูปมาตรวัดเลขไมล์';
      if (!claimForm.odo_in) e.odo_in = 'กรุณาระบุเลขไมล์';
    }
    return e;
  };

  const handleClaimSubmit = async (fetchFuelExpenses) => {
    const errors = validateClaimForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    if (claimSubmitting) return;
    setClaimSubmitting(true);
    setUploadProgress(0);
    try {
      const plate = claimForm.vehicle?.license_plate || 'ไม่ระบุ';
      const dateStr = claimForm.date
        ? dayjs(claimForm.date).format('D/MM/') + String(dayjs(claimForm.date).year() + 543).slice(-2)
        : '-';
      const amountStr = Number(claimForm.fuel_amount || 0).toLocaleString('th-TH');

      // ชื่อรายการ (name) — pattern คงที่ทุกรายการเบิกน้ำมัน:
      // {วันที่เติมน้ำมัน} ค่าน้ำมันรถหน้า{ชื่อโครงการ} ใบกำกับ {เลขใบกำกับ}
      // ป้ายทะเบียน {ยี่ห้อ/รุ่นรถ} {ทะเบียน} เลขไมล์ {เลขไมล์} Km. จำนวน {ยอดเงิน} บาท
      // ค่าไหนไม่มีข้อมูลให้ใส่ "-" แทน
      const projectName = claimForm.project?.name || '-';
      const invoiceNumberStr = claimForm.invoice_number || '-';
      const vehicleBrandModel = claimForm.vehicle
        ? `รถ${claimForm.vehicle.brand || ''}${claimForm.vehicle.model_name ? ` ${claimForm.vehicle.model_name}` : ''}`.trim() || '-'
        : '-';
      const plateForName = claimForm.vehicle?.license_plate || '-';
      const odoStr = claimForm.odo_in || '-';

      const name = `${dateStr} ค่าน้ำมันรถ ${projectName} ใบกำกับ ${invoiceNumberStr} ป้ายทะเบียน ${vehicleBrandModel} ${plateForName} เลขไมล์ ${odoStr} Km. จำนวน ${amountStr} บาท`;
      const desc = dateStr !== '-' ? `วันที่เติมน้ำมัน ${dateStr}` : '';

      // ยึด fuel_amount (ยอดเงินในบิล) เป็นยอดจริงเสมอ แล้ว back-calculate base/vat จากยอดนี้
      // แทนที่จะใช้ค่า base_amount/vat_amount ที่ OCR อ่านแยกจากบิล (คนละจุดในบิล อาจอ่านผิดจนไม่รวมกันเท่ากับ fuel_amount)
      const totalAmount = Number(claimForm.fuel_amount) || 0;
      const baseAmount = Math.round((totalAmount / 1.07) * 100) / 100;
      const vatAmount = Math.round((totalAmount - baseAmount) * 100) / 100;

      const payload = {
        name,
        price: totalAmount,
        date: claimForm.payment_date || claimForm.date,
        employee: me?.userData?._id,
        project: claimForm.project?._id || null,
        budget: claimForm.budget?._id || null,
        customer: claimForm.customer?._id || null,
        payee: claimForm.payee,
        description: desc.trim(),
        expenses_type: claimForm.expenses_type || 'REFUND',
        isFuel: true,
        fuel_liters: claimForm.fuel_liters !== '' ? Number(claimForm.fuel_liters) : null,
        fuel_price_per_liter: claimForm.fuel_price_per_liter !== '' ? Number(claimForm.fuel_price_per_liter) : null,
        fuel_station: claimForm.fuel_station || null,
        fuel_fill_date: claimForm.date || null,
        fuel_license_plate_in_bill: claimForm.fuel_license_plate_in_bill || plate,
        fuel_type_in_bill: claimForm.fuel_type_in_bill || claimForm.vehicle?.fuel_type || null,
        fuel_odometer: claimForm.odo_in !== '' ? Number(claimForm.odo_in) : null,
        vat_amount: vatAmount,
        base_amount: baseAmount,
        includes_vat: totalAmount > 0,
        net_price: totalAmount,
        invoice_number: claimForm.invoice_number || null,
      };

      const imagesToUpload = [];
      if (claimForm.bill_image) imagesToUpload.push(dataUrlToFile(claimForm.bill_image, 'bill_image.jpg'));
      if (claimForm.odo_image) imagesToUpload.push(dataUrlToFile(claimForm.odo_image, 'odo_image.jpg'));
      (claimForm.other_images || []).forEach((img, idx) =>
        imagesToUpload.push(dataUrlToFile(img, `other_image_${idx}.jpg`)),
      );

      if (imagesToUpload.length > 0) {
        // resize ทุกไฟล์ให้เสร็จก่อน เพื่อรู้ total bytes จริงที่จะอัป แล้วค่อย
        // อัปพร้อมรายงาน % (ถ่วงน้ำหนักตามขนาดไฟล์) ให้ user เห็นว่าเหลืออีกนานแค่ไหน
        const resizedFiles = await Promise.all(imagesToUpload.map(resizeImageFileIfNeeded));
        const totalBytes = resizedFiles.reduce((sum, f) => sum + f.size, 0) || 1;
        const loadedBytes = new Array(resizedFiles.length).fill(0);
        const reportProgress = () => {
          const loaded = loadedBytes.reduce((a, b) => a + b, 0);
          setUploadProgress(Math.min(99, Math.round((loaded / totalBytes) * 100)));
        };

        const uploadedIds = await Promise.all(
          resizedFiles.map(async (file, idx) => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            const { data } = await api.post(
              `${process.env.REACT_APP_API_URL}/expenses/upload-image`,
              formData,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (evt) => {
                  loadedBytes[idx] = evt.loaded;
                  reportProgress();
                },
              },
            );
            return data.id;
          }),
        );
        setUploadProgress(100);
        payload.bill_images = uploadedIds.filter(Boolean);
      }

      await dispatch(actions.expensesCreate(payload));
      setClaimOpen(false);
      setClaimSuccess(true);
      setTimeout(() => setClaimSuccess(false), 3000);
      fetchFuelExpenses();
    } catch (e) {
      console.error(e);
    } finally {
      setClaimSubmitting(false);
      setUploadProgress(0);
    }
  };

  return {
    claimOpen,
    setClaimOpen,
    claimForm,
    setClaimForm,
    uploadProgress,
    ocrScanning,
    ocrResult,
    setOcrResult,
    ocrOdoScanning,
    claimSubmitting,
    formErrors,
    setFormErrors,
    plateNotFound,
    setPlateNotFound,
    claimSuccess,
    needsOdo,
    openClaim,
    handleScanBill,
    handleScanOdometer,
    handleProjectChange,
    handleBudgetChange,
    handleVehicleChange,
    handleCustomerChange,
    handleExpensesTypeChange,
    handlePaymentDateChange,
    handleOdoChange,
    handleDateChange,
    handleAmountChange,
    customerFilterOptions,
    openCustomerModal,
    bank,
    setBank,
    customerSubmitting,
    customerControl,
    customerErrors,
    handleCustomerSubmit,
    handleOpenCustomerModal,
    handleCloseCustomerModal,
    onSubmitCustomer,
    handleClaimSubmit,
  };
}
